import React, { useCallback, useState } from 'react';
import { Stack, ControlLink, Text, Overlay } from '@moda/om';
import { throwQueryError } from '../../../lib/apollo/throwQueryError';
import { reportError } from '../../../lib/reportError';
import { PaymentSource } from '../../../constants';
import { useErrorModal } from '../../../hooks/useErrorModal';
import {
  useCart,
  useUpdateCartMutation,
  CartUpdateAction,
  CartUpdatePropertyName
} from '../../../hooks/useCart';
import { useAddressesQuery } from '../../../hooks/useAddress';
import { LoadingBalls } from '../../LoadingBalls';
import { AmazonShippingAddressPreview } from '../AmazonShippingAddressPreview';
import { AmazonPaymentMethodPreview } from '../AmazonPaymentMethodPreview';
import './AmazonShippingAndPaymentPreview.scss';

type Props = {
  onEdit: () => void;
};

enum Mode {
  Resting,
  Switching
}

export const AmazonShippingAndPaymentPreview: React.FC<Props> = ({ onEdit }) => {
  const { showErrorModal } = useErrorModal();
  const { data: addresses, error: addressesError } = useAddressesQuery();
  const {
    cart: {
      attributes: { addressId }
    }
  } = useCart();
  const [updateCart] = useUpdateCartMutation();
  const [mode, setMode] = useState(Mode.Resting);

  const handleSwitchToAdyen = useCallback(async () => {
    setMode(Mode.Switching);
    try {
      await updateCart(CartUpdateAction.PaymentSource, {
        [CartUpdatePropertyName.PaymentSource]: PaymentSource.Adyen
      });
    } catch (error) {
      reportError(error);
      showErrorModal();
    }
    setMode(Mode.Resting);
  }, [setMode, updateCart, showErrorModal]);

  const address = addresses.find(address => address.id === String(addressId));

  if (addressesError) throwQueryError(addressesError);

  if (!address) return null;

  return (
    <>
      <div className="AmazonShippingAndPaymentPreview">
        <div className="AmazonShippingAndPaymentPreview__section">
          <AmazonShippingAddressPreview address={address} />
        </div>
        <div className="AmazonShippingAndPaymentPreview__section">
          <AmazonPaymentMethodPreview />
        </div>
        <Stack
          className="AmazonShippingAndPaymentPreview__actions"
          space={4}
          direction="horizontal"
        >
          <ControlLink onClick={handleSwitchToAdyen}>I do not want to pay with Amazon</ControlLink>
          <ControlLink onClick={onEdit}>Edit</ControlLink>
        </Stack>
      </div>

      <Overlay show={mode === Mode.Switching}>
        <LoadingBalls />
        <Text treatment="eyebrow">Switching</Text>
      </Overlay>
    </>
  );
};
